import { Component, Output, EventEmitter, Input, HostListener, ElementRef } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { flatMap, uniqBy, find, isNil } from 'lodash';
import { tap } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { EnergyDataStoreService } from '@app/shared/services/store/energy-data-store.service';
import { MarketOperators, MarketOperatorsFilter } from '@app/shared/models/meter-data/meter-operator-filter';
import { MeterDataService } from '@app/shared/services/api/meter-data.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { FilterOptionTypes } from '@app/shared/enums';
import { MeterDataStoreServiceDirective } from '@app/shared/services/store/meter-data-store.service';
import { EnergyTypes, EnergyTypesFilter, EnergyTypesGroupingByKeys } from '@app/shared/models/meter-data/energy-types-filter';
import { MeterTypes, MeterTypesFilter } from '@app/shared/models/meter-data/meter-types-filter';

@Component({
  selector: 'app-filter-options',
  templateUrl: './filter-options.component.html',
  styleUrls: ['./filter-options.component.scss'],
})
export class FilterOptionsComponent {
  labelPosition = 'after';
  color = 'primary';
  showFilter = false;
  filterOptions: any[] = [];
  municipalityKey?: string;

  energyTypesFilter = Array<EnergyTypesFilter>();
  meterTypesFilter = Array<MeterTypesFilter>();
  marketOperatorFilter = Array<MarketOperatorsFilter>();

  @Input() isInMeterWizard = false;
  @Output() filterEnergyType = new EventEmitter<Array<string>>();
  @Output() filterMeterType = new EventEmitter<Array<string>>();
  @Output() filterMarketOperator = new EventEmitter<Array<string>>();

  energyTypes: any[] = [];
  meterTypes: any[] = [];
  marketOperators: any[] = [];

  constructor(
    public meterDataStoreServiceDirective: MeterDataStoreServiceDirective,
    private energyDataStore: EnergyDataStoreService,
    private _meterDataService: MeterDataService,
    private _userMunicipalitiesService: UserMunicipalitiesService,
    private overlayLoaderService: OverlayLoaderService,
    private elementRef: ElementRef
  ) {}

  @HostListener('document:click', ['$event'])
  clickout(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.showFilter = false;
    }
  }

  loadItems() {
    this.marketOperators = this._meterDataService.meterFilterOptions
      .filter((options) => options.type === FilterOptionTypes.MSB)
      .map((data) => {
        let isOn = true;

        if (this.energyDataStore.filter.marketOperators.length > 0) {
          isOn = this.energyDataStore.filter.marketOperators.includes(data.msbid);
        }

        return {
          name: data.name,
          value: data.msbid,
          on: isOn,
        };
      });

    return forkJoin([this.meterDataStoreServiceDirective.getMeterTypes(), this.meterDataStoreServiceDirective.getEnergyTypes()]).pipe(
      tap((response: any) => {
        this.meterTypes = uniqBy(response[0], (mt) => mt['sortOrder']);
        this.meterTypes = uniqBy(this.meterTypes, (mt) => mt['name']);

        this.energyTypes = uniqBy(response[1], (et) => et['sortOrder']);
        this.energyTypes = uniqBy(this.energyTypes, (mt) => mt['name']);
      })
    );
  }

  loadFilterOptions() {
    if (this.municipalityKey !== this._userMunicipalitiesService.currentMunicipality.key) {
      this.municipalityKey = this._userMunicipalitiesService.currentMunicipality.key;

      this.overlayLoaderService.isLoading = true;
      this.filterOptions = this._meterDataService.meterFilterOptions.map((data) => {
        return {
          lineOfBusiness: data.lineOfBusiness,
          msbCategory: data.msbCategory,
          msbid: data.msbid,
          municipalityKey: data.municipalityKey,
          name: data.name,
          type: data.type,
        };
      });
      this.setSelected();
      this.overlayLoaderService.isLoading = false;
    }
  }

  setSelected() {
    const meterTypesGroup = this.meterTypes.map((x) => this.toFilterItem(x, 'meter'));
    this.meterTypesFilter = [
      new MeterTypesFilter(
        'Zählertyp',
        meterTypesGroup,
        meterTypesGroup.some((x) => x.isVisible)
      ),
    ];

    const sparteGroup = this.energyTypes
      .filter((et: any) => EnergyTypesGroupingByKeys['Sparte'].includes(et.keywords))
      .map((x) => this.toFilterItem(x, 'energy'));
    const erzeugerGroup = this.energyTypes
      .filter((et: any) => EnergyTypesGroupingByKeys['Erzeuger'].includes(et.keywords))
      .map((x) => this.toFilterItem(x, 'energy'));
    const verbraucherGroup = this.energyTypes
      .filter((et: any) => EnergyTypesGroupingByKeys['Verbraucher'].includes(et.keywords))
      .map((x) => this.toFilterItem(x, 'energy'));

    this.energyTypesFilter = [
      new EnergyTypesFilter(
        'Sparte',
        sparteGroup,
        sparteGroup.some((x) => x.isVisible)
      ),
      new EnergyTypesFilter(
        'Erzeuger',
        erzeugerGroup,
        erzeugerGroup.some((x) => x.isVisible)
      ),
      new EnergyTypesFilter(
        'Verbraucher',
        verbraucherGroup,
        verbraucherGroup.some((x) => x.isVisible)
      ),
    ];

    if (this.energyDataStore.filter.marketOperators.length > 0) {
      this.energyDataStore.filter.marketOperators.forEach((savedMarketOperator) => {
        const _filterMarketOperator = find(this.marketOperators, (x) => x.value === savedMarketOperator);
        const isOn = !isNil(_filterMarketOperator);
        _filterMarketOperator.on = isOn;
      });
    }

    const marketOperatorGroup = this.marketOperators.map((x) => this.toFilterMarketOperatorItem(x));

    this.marketOperatorFilter = [
      new MarketOperatorsFilter(
        'Messstellenbetreiber',
        marketOperatorGroup,
        marketOperatorGroup.some((x) => x.isVisible)
      ),
    ];
  }

  toFilterItem(data: any, filterType: string) {
    let filterData: string[] = [];
    let isOn = true;
    let isVisible = true;
    if (filterType === 'meter') {
      filterData = this.energyDataStore.filter.meterTypes;
    }

    if (filterType === 'energy') {
      filterData = this.energyDataStore.filter.energyTypes;
    }

    const findData = find(filterData, (res) => {
      return res === data.name;
    });

    const findDatab = find(this.filterOptions, (res) => {
      return res.name === data.name;
    });

    isOn = !isNil(findData);
    if (this.filterOptions.length > 0) {
      isVisible = !isNil(findDatab);
    } else {
      isVisible = false;
    }

    return {
      name: data.name,
      icon: data.imageName,
      on: isOn,
      isVisible,
    };
  }

  toFilterMarketOperatorItem(data: any) {
    const findDatab = find(this.filterOptions, (res: any) => {
      return res.name === data.name;
    });
    let isVisible;

    if (this.filterOptions.length > 0) {
      isVisible = !isNil(findDatab);
    } else {
      isVisible = false;
    }

    return {
      name: data.name,
      value: data.value,
      on: data.on,
      isVisible,
    };
  }

  toggleEnergyType(event: MatSlideToggleChange, energyTypesFilter: EnergyTypesFilter) {
    energyTypesFilter.energyTypes
      .filter((energyType: EnergyTypes) => energyType.isVisible)
      .forEach((energyType: EnergyTypes) => (energyType.on = event.checked));
    this.emitEnergyFilter();
  }

  private emitEnergyFilter() {
    const energyTypeNames = this.getSelectedEnergyType();
    this.filterEnergyType.emit(energyTypeNames);
  }

  getSelectedEnergyType(): Array<string> {
    return flatMap(this.energyTypesFilter, (eFilter) => eFilter.energyTypes)
      .filter((energyType) => energyType.on)
      .map((energyType) => energyType.name);
  }

  getEnergyTypes(): Array<string> {
    return this.energyTypes.map((x) => x.name);
  }

  toggleMarketOperator(event: MatSlideToggleChange, marketOperatorFilter: MarketOperatorsFilter) {
    marketOperatorFilter.marketOperators
      .filter((marketOperator: MarketOperators) => marketOperator.isVisible)
      .forEach((marketOperator: MarketOperators) => (marketOperator.on = event.checked));
    this.emitMarketOperatorFilter();
  }

  private emitMarketOperatorFilter() {
    const marketOperatorValues = this.getSelectedMarketOperator();
    this.filterMarketOperator.emit(marketOperatorValues);
  }

  getSelectedMarketOperator(): Array<string> {
    return flatMap(this.marketOperatorFilter, (oFilter) => oFilter.marketOperators)
      .filter((marketOperator) => marketOperator.on)
      .map((marketOperator) => marketOperator.value);
  }

  getMarketOperatorTypes(): Array<string> {
    return this.marketOperators.map((x) => x.value);
  }

  toggleMeterType(event: MatSlideToggleChange, meterTypesFilter: MeterTypesFilter) {
    meterTypesFilter.meterTypes.filter((meterType: MeterTypes) => meterType.isVisible).forEach((meterType: MeterTypes) => (meterType.on = event.checked));
    this.emitMeterFilter();
  }

  private emitMeterFilter() {
    const meterTypeNames = this.getSelectedMeterType();
    this.filterMeterType.emit(meterTypeNames);
  }

  getSelectedMeterType(): Array<string> {
    return flatMap(this.meterTypesFilter, (eFilter) => eFilter.meterTypes)
      .filter((energyType) => energyType.on)
      .map((energyType) => energyType.name);
  }

  getMeterTypes(): Array<string> {
    return this.meterTypes.map((x) => x.name);
  }

  checkChanged(filterTriggerBy: number) {
    switch (filterTriggerBy) {
      case 1:
        this.emitEnergyFilter();
        break;
      case 2:
        this.emitMeterFilter();
        break;
      case 3:
        this.emitMarketOperatorFilter();
        break;
    }
  }
}
